// Müşteri paneli görüntüleme şalteri (view_as.enabled).
//
// Volkan'ın "ters gittiğini görürsem komple kapatayım" şartının karşılığı:
// tek tıkla açılıp kapanır, anında geçerli olur, yeni sürüm gerektirmez.
//
// Fail-closed: ayar okunamazsa ekran KAPALI gösterir — sunucudaki kuralın
// aynısı (backend/app/services/view_as_policy.py). Ekran "açık" derken sunucu
// "kapalı" davransaydı en kötü türden yanlış güven olurdu.
//
// Tasarım: docs/superpowers/specs/2026-09-06-admin-view-as-org-design.md §5.5

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useT } from '../../i18n'
import { viewAsI18n } from '../../i18n/settings/view-as'
import { apiUrl } from '../../lib/api'

const CONFIG_KEY = 'view_as.enabled'
const QUERY_KEY = ['admin', 'config', CONFIG_KEY] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** `true` yalnızca satır açıkça true ise; şekil bozuksa false. */
function parseEnabled(payload: unknown): boolean {
  if (!isRecord(payload) || !isRecord(payload.config_value)) return false
  return payload.config_value.value === true
}

type ReadResult = { enabled: boolean; readFailed: boolean }

async function readEnabled(): Promise<ReadResult> {
  const res = await fetch(apiUrl(`/admin/config/${CONFIG_KEY}`))
  // 404 = satır hiç yazılmamış: bu bir arıza değil, normal başlangıç hali.
  if (res.status === 404) return { enabled: false, readFailed: false }
  if (!res.ok) return { enabled: false, readFailed: true }
  try {
    return { enabled: parseEnabled(await res.json()), readFailed: false }
  } catch {
    return { enabled: false, readFailed: true }
  }
}

async function writeEnabled(next: boolean): Promise<boolean> {
  const res = await fetch(apiUrl(`/admin/config/${CONFIG_KEY}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config_value: { value: next }, value_type: 'boolean' }),
  })
  if (!res.ok) throw new Error(`view_as.enabled write failed: ${res.status}`)
  return next
}

export function ViewAsControl() {
  const t = useT(viewAsI18n)
  const queryClient = useQueryClient()

  const { data, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: readEnabled,
  })

  const mutation = useMutation({
    mutationFn: writeEnabled,
    // Kasıtlı olarak iyimser güncelleme YOK: yazma başarısızsa şalter açık
    // görünüp sunucuda kapalı kalırdı. Gerçek durum sunucudan gelir.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const enabled = data?.enabled ?? false
  const readFailed = isError || (data?.readFailed ?? false)

  return (
    <section
      className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-4"
      data-testid="view-as-control"
    >
      <h3 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h3>
      <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.description}</p>

      <label className="mt-4 flex items-center gap-3">
        <input
          type="checkbox"
          role="switch"
          checked={enabled}
          disabled={mutation.isPending}
          onChange={(e) => mutation.mutate(e.target.checked)}
          className="h-5 w-5 cursor-pointer"
        />
        <span className="text-sm font-medium text-[var(--color-text)]">
          {t.switchLabel}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {enabled ? t.on : t.off}
        </span>
      </label>

      <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t.readOnlyNote}</p>

      {readFailed && (
        <p className="mt-2 text-xs text-[var(--color-danger)]" role="status">
          {t.loadError}
        </p>
      )}
      {mutation.isError && (
        <p className="mt-2 text-xs text-[var(--color-danger)]" role="alert">
          {t.saveError}
        </p>
      )}
    </section>
  )
}
